/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { color } from '@coursera/coursera-ui';

import CourseActionBox from 'bundles/course-cards/components/course-card/enterprise/CourseActionBox';
import type { PropsForWithModal as PropsForCourseAndPartnerNameWithModal } from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseAndPartnerName, {
  CourseAndPartnerNameWithModal,
} from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseCardAutoenrollmentDisclaimer from 'bundles/course-cards/components/course-card/enterprise/CourseCardAutoenrollmentDisclaimer';
import CourseHomeLink from 'bundles/course-cards/components/course-card/enterprise/CourseHomeLink';
import CourseSessionInfoString from 'bundles/course-cards/components/course-card/enterprise/CourseSessionInfoString';
import SkillPathwayTakeAssessmentButton from 'bundles/course-cards/components/course-card/enterprise/SkillPathwayTakeAssessmentButton';
import StaticLinearProgress from 'bundles/course-cards/components/course-card/enterprise/StaticLinearProgress';
import { getEnterpriseCourseHomeLinkLabel } from 'bundles/course-cards/utils/courseUtils';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import type OnDemandLearnerMaterialsV1 from 'bundles/naptimejs/resources/onDemandLearnerMaterials.v1';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type OnDemandLearnerSessionsV1 from 'bundles/naptimejs/resources/onDemandLearnerSessions.v1';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import _t from 'i18n!nls/course-cards';

const styles = {
  skillPathwayActionBox: css`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    ${breakpoints.down('xs')} {
      align-items: center;
    }
  `,
};

type Props = {
  course: CoursesV1;
  cardDropdownWidget: JSX.Element;
  learnerSession?: OnDemandLearnerSessionsV1;
  learnerMaterials?: OnDemandLearnerMaterialsV1;
  isAutoenrolled?: boolean;
  isPartOfS12n?: boolean;
} & Omit<PropsForCourseAndPartnerNameWithModal, 'courseId' | 'courseName' | 'partnerIds'>;

const CourseEnrolledFuturePublicSessionCard: React.FC<Props> = ({
  course: { id, name, homeLink, partnerIds },
  cardDropdownWidget,
  learnerSession,
  learnerMaterials,
  isAutoenrolled,
  isPartOfS12n,
  ...rest
}) => {
  const startDateString = learnerSession && learnerSession.startDateString;
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const program = useProgram();
  const isSkillPathway = badgeMetadata?.isSkillPathway;

  return (
    <div className="rc-CourseEnrolledFuturePublicSessionCard row flex-1">
      <div className="col-xs-12 col-sm-8 col-md-9 pos-static-override-hack">
        {isSkillPathway ? (
          <CourseAndPartnerName
            courseId={id}
            courseName={name}
            partnerIds={partnerIds}
            isPartOfS12n={isPartOfS12n}
            onClick={() =>
              program?.metadata?.slug &&
              badgeMetadata?.badge &&
              setLocation(`/programs/${program?.metadata?.slug}/learning-path/${badgeMetadata.badge.slug}`)
            }
            {...rest}
          />
        ) : (
          <CourseAndPartnerNameWithModal
            courseId={id}
            courseName={name}
            partnerIds={partnerIds}
            isPartOfS12n={isPartOfS12n}
            onClick={() => setLocation(homeLink)}
            {...rest}
          />
        )}
        {cardDropdownWidget}
        {learnerMaterials && (
          <div>
            <Typography2 component="span" variant="subtitleMedium">
              <CourseSessionInfoString courseId={id} startDateString={startDateString} />
            </Typography2>
            {isPartOfS12n && (
              <Typography2 component="p" className="m-b-1" variant="bodyPrimary">
                {_t('Started')}
              </Typography2>
            )}
            <StaticLinearProgress
              progress={learnerMaterials.summaryCoreProgressPercentage ?? 0}
              color={color.success}
              aria-label={name}
            />
            {isAutoenrolled && <CourseCardAutoenrollmentDisclaimer />}
          </div>
        )}
      </div>
      <CourseActionBox>
        {isSkillPathway ? (
          <div css={styles.skillPathwayActionBox}>
            <CourseHomeLink
              homeLink={homeLink}
              label={getEnterpriseCourseHomeLinkLabel(learnerMaterials?.summaryCoreProgressPercentage)}
              courseName={name}
            />
            <SkillPathwayTakeAssessmentButton courseId={id} />
          </div>
        ) : (
          <div>
            <CourseHomeLink
              homeLink={homeLink}
              label={getEnterpriseCourseHomeLinkLabel(learnerMaterials?.summaryCoreProgressPercentage)}
              courseName={name}
            />
          </div>
        )}
      </CourseActionBox>
    </div>
  );
};

export default CourseEnrolledFuturePublicSessionCard;
